import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ApiError } from './api';

// ---------- aviso rápido (toast) ----------
const ToastCtx = createContext<(msg: string) => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState('');
  const [show, setShow] = useState(false);
  const timer = useRef<number>();
  const el = useRef<HTMLDivElement>(null);
  const toast = useCallback((m: string) => {
    setMsg(m); setShow(true);
    // A faixa entra na camada de cima (popover): assim aparece também sobre uma janela aberta (detalhes, confirmação),
    // que de outro modo cobriria a mensagem de erro e o botão pareceria não responder
    const d = el.current as (HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void }) | null;
    try { if (d?.showPopover) { if (d.matches(':popover-open')) d.hidePopover!(); d.showPopover(); } } catch { /* navegador sem popover: fica como antes */ }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShow(false), Math.min(12000, 4200 + m.length * 25));
  }, []);
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div ref={el} {...{ popover: 'manual' }} className={'toast' + (show ? ' show' : '')} role="status" aria-live="polite">{msg}</div>
    </ToastCtx.Provider>
  );
}

/** Mostra a mensagem de erro da API (regras de negócio) ou uma genérica. */
export function useFail() {
  const toast = useToast();
  return useCallback((err: unknown) => toast(err instanceof ApiError ? err.message : 'Não foi possível concluir. Verifique a conexão e tente de novo.'), [toast]);
}

// ---------- diálogo nativo (<dialog>) ----------
export function Modal({ open, onClose, className = 'dlg', labelledBy, children }: { open: boolean; onClose: () => void; className?: string; labelledBy?: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current; if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return <dialog ref={ref} className={className} aria-labelledby={labelledBy} onClose={onClose} onCancel={e => { e.preventDefault(); onClose(); }}>{open && children}</dialog>;
}

// ---------- ícones ----------
const svg = (size: number, children: ReactNode, sw = 2) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
export const IconKey = ({ size = 15 }: { size?: number }) => svg(size, <><circle cx="7.5" cy="15.5" r="4.5" /><path d="M10.7 12.3L21 2M16 7l3 3M18 5l2 2" /></>);
export const IconClock = () => svg(15, <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>);
export const IconInfo = () => svg(15, <><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></>);
export const IconWarn = () => svg(15, <><path d="M12 9v4M12 17h.01" /><circle cx="12" cy="12" r="9" /></>);
export const IconTeams = () => svg(12, <><path d="M15 10l5-3v10l-5-3" /><rect x="3" y="6" width="12" height="12" rx="2" /></>, 2.2);
export const IconRepeat = () => svg(12, <><path d="M17 2l4 4-4 4" /><path d="M3 11V9a3 3 0 0 1 3-3h15" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v2a3 3 0 0 1-3 3H3" /></>, 2.2);
export const IconBell = () => svg(18, <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>);

const IconMoon = () => svg(18, <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />);
const IconSun = () => svg(18, <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>);

/** Modo claro ou escuro: segue o sistema até a pessoa escolher; a escolha fica guardada neste navegador. */
export function ThemeToggle() {
  const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
  const read = () => { try { return localStorage.getItem('conves-theme'); } catch { return null; } };
  const [theme, setTheme] = useState<string | null>(read);
  const [systemDark, setSystemDark] = useState(!!media?.matches);
  useEffect(() => {
    if (!media) return;
    const on = () => setSystemDark(media.matches);
    media.addEventListener('change', on);
    return () => media.removeEventListener('change', on);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (theme === 'dark' || theme === 'light') document.documentElement.dataset.theme = theme;
    else delete document.documentElement.dataset.theme;
  }, [theme]);
  const dark = theme ? theme === 'dark' : systemDark;
  const label = dark ? 'Mudar para o modo claro' : 'Mudar para o modo escuro';
  return (
    <button type="button" className="bell" aria-label={label} title={label} onClick={() => {
      const t = dark ? 'light' : 'dark';
      try { localStorage.setItem('conves-theme', t); } catch { /* navegador sem armazenamento: vale só nesta visita */ }
      setTheme(t);
    }}>{dark ? <IconSun /> : <IconMoon />}</button>
  );
}

export const Empty = ({ children, pad }: { children: ReactNode; pad?: boolean }) => <div className="empty" style={pad ? { padding: 24 } : undefined}>{children}</div>;
export const Loading = () => <div className="empty">Carregando…</div>;
